// src/services/crmService.js
import { supabase } from './supabase';
import { leadService } from './leadService';

export const CRM_STAGES = [
  { key: 'New', label: 'New Lead', color: '#EF4444' },
  { key: 'Contacted', label: 'Contacted', color: '#F59E0B' },
  { key: 'Visit scheduled', label: 'Site Visit', color: '#A8080C' },
  { key: 'Closed', label: 'Won / Closed', color: '#10B981' },
  { key: 'Not interested', label: 'Lost / Inactive', color: '#6B7280' }
];

export const CRM_PRIORITIES = ['Hot', 'Warm', 'Cold'];

export const crmService = {
  /**
   * Fetch all unified CRM leads.
   * Tries public.view_crm_all_leads first; if view is not migrated yet,
   * gracefully combines enquiries, property_solution_leads, and leads.
   */
  async getAllCrmLeads() {
    try {
      const { data, error } = await supabase
        .from('view_crm_all_leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error && data) {
        return { data, error: null };
      }
    } catch {
      // Fallback below
    }

    // Graceful fallback combining source tables
    try {
      const [enqsRes, solRes, srvRes, staffRes] = await Promise.all([
        leadService.getLeads(),
        leadService.getSolutionLeads(),
        leadService.getServiceLeads(),
        leadService.getAdmins(),
      ]);

      const staffMap = new Map();
      (staffRes?.data || []).forEach(a => staffMap.set(a.id, a.full_name));

      const unified = [];

      // 1. Enquiries
      (enqsRes?.data || []).forEach(e => {
        unified.push({
          lead_id: e.id,
          source_table: 'enquiries',
          lead_category: 'Property Enquiry',
          source_channel: e.source || 'app',
          customer_name: e.buyer?.full_name || 'Anonymous Buyer',
          customer_phone: e.buyer?.phone_number || '',
          customer_email: null,
          requirement: e.message || e.property?.title || 'Property Enquiry',
          property_id: e.property_id,
          property_title: e.property?.title || '',
          city: e.property?.city || 'Ghazipur',
          property_type: null,
          budget: null,
          details: e.message,
          status: e.status === 'pending' ? 'New' : e.status === 'accepted' ? 'Contacted' : (e.status || 'New'),
          priority: e.priority || 'Warm',
          assigned_to: e.assigned_to,
          assigned_to_name: staffMap.get(e.assigned_to) || null,
          assigned_at: e.assigned_at,
          follow_up_date: e.follow_up_date,
          seller_id: e.seller_id,
          seller_name: e.seller?.full_name || null,
          seller_phone: e.seller?.phone_number || null,
          created_at: e.created_at,
          updated_at: e.updated_at || e.created_at
        });
      });

      // 2. Property Solution Modal leads
      (solRes?.data || []).forEach(ps => {
        unified.push({
          lead_id: ps.id,
          source_table: 'property_solution_leads',
          lead_category: 'Solution Modal',
          source_channel: ps.source || 'website_modal',
          customer_name: ps.name,
          customer_phone: ps.mobile,
          customer_email: null,
          requirement: ps.requirement,
          property_id: null,
          property_title: null,
          city: ps.city || 'Ghazipur',
          property_type: ps.property_type,
          budget: ps.budget,
          details: ps.details,
          status: ps.status || 'New',
          priority: ps.priority || 'Warm',
          assigned_to: ps.assigned_to,
          assigned_to_name: staffMap.get(ps.assigned_to) || null,
          assigned_at: ps.assigned_at,
          follow_up_date: ps.follow_up_date,
          seller_id: null,
          seller_name: null,
          seller_phone: null,
          created_at: ps.created_at,
          updated_at: ps.updated_at || ps.created_at
        });
      });

      // 3. General service leads
      (srvRes?.data || []).forEach(l => {
        unified.push({
          lead_id: l.id,
          source_table: 'leads',
          lead_category: l.type || 'Service Lead',
          source_channel: l.source || 'website',
          customer_name: l.name || 'Customer',
          customer_phone: l.mobile,
          customer_email: l.email,
          requirement: l.requirement || l.type || 'Service Request',
          property_id: null,
          property_title: null,
          city: l.city || 'Ghazipur',
          property_type: null,
          budget: null,
          details: l.requirement,
          status: l.status || 'New',
          priority: l.priority || 'Warm',
          assigned_to: l.assigned_to,
          assigned_to_name: staffMap.get(l.assigned_to) || null,
          assigned_at: l.assigned_at,
          follow_up_date: l.follow_up_date,
          seller_id: null,
          seller_name: null,
          seller_phone: null,
          created_at: l.created_at,
          updated_at: l.updated_at || l.created_at
        });
      });

      unified.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return { data: unified, error: null };
    } catch (err) {
      console.error('Error fetching fallback CRM leads:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Update lead status, assignment, priority, or follow-up date with activity logging.
   */
  async updateLead(leadId, sourceTable, payload) {
    try {
      // Try stored procedure first
      const { data, error } = await supabase.rpc('crm_update_lead', {
        p_lead_id: leadId,
        p_source_table: sourceTable,
        p_status: payload.status || null,
        p_priority: payload.priority || null,
        p_assigned_to: payload.assigned_to || null,
        p_follow_up_date: payload.follow_up_date || null,
        p_activity_note: payload.note || null,
        p_activity_type: payload.activity_type || 'note'
      });

      if (!error && data?.success) {
        return { data, error: null };
      }
    } catch {
      // Fallback to direct table update below
    }

    try {
      const updates = {};
      if (payload.status) updates.status = payload.status;
      if (payload.priority) updates.priority = payload.priority;
      if (payload.assigned_to !== undefined) {
        updates.assigned_to = payload.assigned_to || null;
        updates.assigned_at = payload.assigned_to ? new Date().toISOString() : null;
      }
      if (payload.follow_up_date) updates.follow_up_date = payload.follow_up_date;
      updates.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from(sourceTable)
        .update(updates)
        .eq('id', leadId)
        .select('id');

      if (error) throw error;

      // Log activity note if provided
      if (payload.note && payload.note.trim()) {
        await this.addActivity(leadId, sourceTable, {
          activity_type: payload.activity_type || 'note',
          notes: payload.note.trim(),
          new_status: payload.status || null
        });
      }

      return { data, error: null };
    } catch (err) {
      console.error('Error updating CRM lead:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Fetch activity timeline for a lead.
   */
  async getLeadActivities(leadId) {
    try {
      const { data, error } = await supabase
        .from('crm_lead_activities')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error getting lead activities:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Add a call note or activity entry for a lead.
   */
  async addActivity(leadId, sourceTable, activity) {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const currentUserId = sessionData?.session?.user?.id;

      let adminName = 'Staff Member';
      if (currentUserId) {
        const { data: adminUser } = await supabase
          .from('admin_users')
          .select('full_name')
          .eq('id', currentUserId)
          .single();
        if (adminUser?.full_name) adminName = adminUser.full_name;
      }

      const { data, error } = await supabase
        .from('crm_lead_activities')
        .insert([{
          lead_id: leadId,
          lead_source_table: sourceTable,
          performed_by: currentUserId || null,
          performer_name: adminName,
          activity_type: activity.activity_type || 'note',
          notes: activity.notes,
          previous_status: activity.previous_status || null,
          new_status: activity.new_status || null
        }]);

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error adding activity:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Get follow-up reminders.
   */
  async getFollowups(assignedTo = null) {
    try {
      let query = supabase
        .from('crm_lead_followups')
        .select('*')
        .order('due_date', { ascending: true });

      if (assignedTo) {
        query = query.eq('assigned_to', assignedTo);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error getting followups:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Add a scheduled follow-up reminder.
   */
  async addFollowup(followupData) {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const currentUserId = sessionData?.session?.user?.id;

      const record = {
        lead_id: followupData.lead_id,
        lead_source_table: followupData.lead_source_table,
        assigned_to: followupData.assigned_to,
        task_title: followupData.task_title,
        due_date: followupData.due_date,
        is_completed: false,
        created_by: currentUserId || null
      };

      const { data, error } = await supabase
        .from('crm_lead_followups')
        .insert([record]);

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error adding followup:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Complete a follow-up.
   */
  async completeFollowup(followupId) {
    try {
      const { data, error } = await supabase
        .from('crm_lead_followups')
        .update({
          is_completed: true,
          completed_at: new Date().toISOString()
        })
        .eq('id', followupId);

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error completing followup:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Staff list for assignment.
   */
  async getStaffList() {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id, full_name, email, role')
        .eq('status', 'Active')
        .order('full_name', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error getting staff list:', err);
      return { data: [], error: err };
    }
  }
};

